// useNudges — shared nudge action for financial users: the Everyone rollup on
// ReceiptsPage and the no-receipt cardholder groups on ReconcilePage.
//
// A person is included by a checkbox. One "Nudge selected" button sends the
// whole check, and it stays locked until at least one person is checked.
// Nothing sends on its own, and a statement upload never reaches this.
//
// Someone nudged inside the rate-limit window comes back 409. The batch asks
// once, for that whole set, before any of them are forced. A 422 means no
// contact channel.

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetchAuthed } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';
import type { NudgeRecent } from './types';
import { buttonClass } from '../components/ui/Button';

// Hours/days-ago caption for a nudge timestamp, e.g. "3h ago" / "2d ago".
export function nudgeCaption(iso: string): string {
  const hours = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export type NudgeTarget = { userId: string; name: string };

export type NudgeProgress = { done: number; total: number };

export function nudgeAgainMessage(names: string[]): string {
  const who = names.length === 1
    ? names[0]
    : names.length === 2
      ? `${names[0]} and ${names[1]}`
      : `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
  return `${who} ${names.length === 1 ? 'was' : 'were'} nudged in the last day. Nudge them again?`;
}

export function nudgeBatchSummary(r: { sent: number; skippedRecent: number; unreachable: number; failed: number }): string {
  const bits: string[] = [];
  if (r.sent > 0) bits.push(r.sent === 1 ? 'Nudged 1 person.' : `Nudged ${r.sent} people.`);
  if (r.skippedRecent > 0) bits.push(r.skippedRecent === 1
    ? '1 was already nudged in the last day and was left alone.'
    : `${r.skippedRecent} were already nudged in the last day and were left alone.`);
  if (r.unreachable > 0) bits.push(r.unreachable === 1
    ? '1 has no contact method.'
    : `${r.unreachable} have no contact method.`);
  if (r.failed > 0) bits.push(r.failed === 1 ? '1 did not go through.' : `${r.failed} did not go through.`);
  return bits.join(' ') || 'Nothing to send.';
}

// Locked until someone is checked. The count is the unlock.
export function NudgeSelectedButton({
  count, sending, progress, onClick,
}: {
  count: number;
  sending: boolean;
  progress: NudgeProgress | null;
  onClick: () => void;
}) {
  const label = sending && progress
    ? `Nudging ${progress.done} of ${progress.total}`
    : count > 0
      ? `Nudge ${count} selected`
      : 'Nudge selected';
  return (
    <button type="button" onClick={onClick} disabled={count === 0 || sending} className={buttonClass('filled', 'sm')}>
      {label}
    </button>
  );
}

type Outcome = 'sent' | 'recent' | 'unreachable' | 'failed';

export default function useNudges(enabled = true) {
  const toast = useToast();
  const confirm = useConfirm();
  const [recent, setRecent] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState<NudgeProgress | null>(null);
  // Set before the first await. The button's disabled flag lands a render later,
  // and a second click in that gap would send the same people twice.
  const inflight = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    apiFetchAuthed('/reconciliation/nudges/recent', { silent: true })
      .then((data: NudgeRecent) => {
        const m: Record<string, string> = {};
        (data?.nudges || []).forEach((n) => { m[n.target_user_id] = n.created_at; });
        setRecent(m);
      })
      .catch(() => {});
  }, [enabled]);

  const postOne = useCallback(async (userId: string, periodId: number | undefined, force: boolean): Promise<Outcome> => {
    try {
      const body: Record<string, unknown> = { user_id: userId };
      if (periodId != null) body.period_id = periodId;
      if (force) body.force = true;
      await apiFetchAuthed('/reconciliation/nudges', { method: 'POST', body, silent: true });
      setRecent((r) => ({ ...r, [userId]: new Date().toISOString() }));
      return 'sent';
    } catch (e: any) {
      if (e?.status === 409) return 'recent';
      if (e?.status === 422) return 'unreachable';
      return 'failed';
    }
  }, []);

  const sendSelected = useCallback(async (targets: NudgeTarget[], periodId?: number) => {
    const seen = new Set<string>();
    const people = targets.filter((t) => {
      if (!t.userId || seen.has(t.userId)) return false;
      seen.add(t.userId);
      return true;
    });
    if (!people.length || inflight.current) return { sentIds: [] as string[] };

    inflight.current = true;
    setSending(true);
    setProgress({ done: 0, total: people.length });
    const sentIds: string[] = [];
    const recentHits: NudgeTarget[] = [];
    let unreachable = 0;
    let failed = 0;
    try {
      for (let i = 0; i < people.length; i++) {
        const outcome = await postOne(people[i].userId, periodId, false);
        if (outcome === 'sent') sentIds.push(people[i].userId);
        else if (outcome === 'recent') recentHits.push(people[i]);
        else if (outcome === 'unreachable') unreachable += 1;
        else failed += 1;
        setProgress({ done: i + 1, total: people.length });
      }

      let skippedRecent = recentHits.length;
      if (recentHits.length) {
        const ok = await confirm({
          title: 'Nudge again?',
          message: nudgeAgainMessage(recentHits.map((t) => t.name || 'this person')),
          confirmLabel: 'Nudge again',
          cancelLabel: 'Leave them',
        });
        if (ok) {
          skippedRecent = 0;
          for (const t of recentHits) {
            const outcome = await postOne(t.userId, periodId, true);
            if (outcome === 'sent') sentIds.push(t.userId);
            else if (outcome === 'unreachable') unreachable += 1;
            else failed += 1;
          }
        }
      }

      const summary = nudgeBatchSummary({ sent: sentIds.length, skippedRecent, unreachable, failed });
      if (sentIds.length > 0 && failed === 0 && unreachable === 0) toast.success(summary);
      else if (sentIds.length > 0) toast.warning(summary);
      else if (failed > 0 || unreachable > 0) toast.error(summary);
      else toast.info(summary);
      return { sentIds };
    } finally {
      inflight.current = false;
      setSending(false);
      setProgress(null);
    }
  }, [postOne, confirm, toast]);

  return { recent, sending, progress, sendSelected };
}
